import { Icon } from "icons";
import { useState } from "react";
import {
  Badge,
  Button,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  Input,
  Label,
} from "ui";

import { ConfigurableFilterOption } from "src/components/ConfigurableFilterOption";
import {
  type FilterAction,
  type FilterGroupConfig,
  getSelectedFilterValues,
  type SelectedFilters,
} from "src/components/filterConfig";

interface ConfigurableFilterGroupProps {
  group: FilterGroupConfig;
  selectedFilters: SelectedFilters;
  onAction: (action: FilterAction) => void;
}

function getInitialRange(
  selectedValue: string | undefined,
  group: FilterGroupConfig,
) {
  const [selectedFrom, selectedTo] = selectedValue?.split(":") ?? [];

  return {
    rangeFrom: selectedFrom ?? String(group.range?.initialFrom ?? 0),
    rangeTo: selectedTo ?? String(group.range?.initialTo ?? 0),
  };
}

export function ConfigurableFilterGroup(props: ConfigurableFilterGroupProps) {
  const selectedValues = getSelectedFilterValues(
    props.selectedFilters,
    props.group.filter,
  );
  const [state, setState] = useState({
    search: "",
    ...getInitialRange(selectedValues[0], props.group),
  });
  const activeCount = selectedValues.filter((value) => {
    return value !== props.group.defaultValue;
  }).length;
  const normalizedSearch = state.search.trim().toLocaleLowerCase();
  const visibleOptions = props.group.search
    ? props.group.options.filter((option) => {
        return option.label.toLocaleLowerCase().includes(normalizedSearch);
      })
    : props.group.options;
  const optionItems = visibleOptions.map((option) => {
    return (
      <ConfigurableFilterOption
        key={option.value}
        group={props.group}
        option={option}
        selected={selectedValues.includes(option.value)}
        onAction={props.onAction}
      />
    );
  });
  const options = (
    <>
      {props.group.emptyOption ? (
        <>
          <ConfigurableFilterOption
            group={props.group}
            option={props.group.emptyOption}
            selected={selectedValues.includes(props.group.emptyOption.value)}
            onAction={props.onAction}
          />
          {optionItems.length > 0 ? <DropdownMenuSeparator /> : null}
        </>
      ) : null}
      {optionItems}
    </>
  );

  const rangeFrom = Number(state.rangeFrom);
  const rangeTo = Number(state.rangeTo);
  const rangeIsValid =
    props.group.range &&
    state.rangeFrom !== "" &&
    state.rangeTo !== "" &&
    Number.isInteger(rangeFrom) &&
    Number.isInteger(rangeTo) &&
    rangeFrom >= props.group.range.min &&
    rangeTo <= props.group.range.max &&
    rangeFrom <= rangeTo;

  function selectRange(value: string) {
    setState({ ...state, ...getInitialRange(value, props.group) });
    props.onAction({
      type: "filter-selected",
      filter: props.group.filter,
      value,
    });
  }

  function applyRange() {
    if (!rangeIsValid) return;

    props.onAction({
      type: "filter-selected",
      filter: props.group.filter,
      value: `${rangeFrom}:${rangeTo}`,
    });
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Icon name={props.group.icon} />
        {props.group.label}
        {activeCount > 0 ? (
          <Badge variant="secondary" className="ml-auto mr-1">
            {activeCount}
          </Badge>
        ) : null}
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent
          className={
            props.group.search || props.group.range ? "w-64 p-0" : "min-w-44"
          }
        >
          {props.group.search ? (
            <>
              <div className="border-b p-1">
                <Input
                  autoFocus
                  value={state.search}
                  onChange={(event) => {
                    setState({ ...state, search: event.target.value });
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Escape") {
                      event.stopPropagation();
                    }
                  }}
                  placeholder={props.group.search.placeholder}
                  className="h-9 border-0 shadow-none focus-visible:ring-0"
                />
              </div>
              <div className="max-h-[300px] overflow-y-auto p-1">
                {optionItems.length > 0 ? (
                  options
                ) : (
                  <div className="py-6 text-center text-sm">
                    {props.group.search.emptyMessage}
                  </div>
                )}
              </div>
            </>
          ) : props.group.range ? (
            <div className="space-y-2 p-1">
              <DropdownMenuRadioGroup
                value={selectedValues[0]}
                onValueChange={selectRange}
              >
                {options}
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <div className="grid grid-cols-2 gap-2 px-2 pb-1">
                <Label className="space-y-1 text-xs">
                  <span>{props.group.range.fromLabel}</span>
                  <Input
                    type="number"
                    min={props.group.range.min}
                    max={props.group.range.max}
                    value={state.rangeFrom}
                    onChange={(event) => {
                      setState({
                        ...state,
                        rangeFrom: event.currentTarget.value,
                      });
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Escape") {
                        event.stopPropagation();
                      }
                    }}
                  />
                </Label>
                <Label className="space-y-1 text-xs">
                  <span>{props.group.range.toLabel}</span>
                  <Input
                    type="number"
                    min={props.group.range.min}
                    max={props.group.range.max}
                    value={state.rangeTo}
                    onChange={(event) => {
                      setState({
                        ...state,
                        rangeTo: event.currentTarget.value,
                      });
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Escape") {
                        event.stopPropagation();
                      }
                    }}
                  />
                </Label>
              </div>
              <Button
                type="button"
                size="sm"
                className="w-full"
                disabled={!rangeIsValid}
                onClick={applyRange}
              >
                {props.group.range.applyLabel}
              </Button>
            </div>
          ) : props.group.selection === "single" ? (
            <DropdownMenuRadioGroup
              value={selectedValues[0]}
              onValueChange={(value) => {
                props.onAction({
                  type: "filter-selected",
                  filter: props.group.filter,
                  value,
                });
              }}
            >
              {options}
            </DropdownMenuRadioGroup>
          ) : (
            options
          )}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}
